import ApiService from '@/services/api'

const state = {
  usuarios: [],
  usuario: null,
  loading: false,
  error: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10
  }
}

const getters = {
  getAllUsuarios: state => state.usuarios,
  getUsuario: state => state.usuario,
  isLoading: state => state.loading,
  getError: state => state.error,
  getPagination: state => state.pagination,
  getUsuarioById: (state) => (id) => {
    return state.usuarios.find(usuario => usuario.id === id)
  }
}

const mutations = {
  SET_USUARIOS(state, usuarios) {
    state.usuarios = usuarios
  },
  SET_USUARIO(state, usuario) {
    state.usuario = usuario
  },
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  SET_ERROR(state, error) {
    state.error = error
  },
  CLEAR_ERROR(state) {
    state.error = null
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = { ...state.pagination, ...pagination }
  },
  ADD_USUARIO(state, usuario) {
    state.usuarios.push(usuario)
  },
  UPDATE_USUARIO(state, usuario) {
    const index = state.usuarios.findIndex(u => u.id === usuario.id)
    if (index !== -1) {
      state.usuarios.splice(index, 1, usuario)
    }
  },
  REMOVE_USUARIO(state, id) {
    state.usuarios = state.usuarios.filter(u => u.id !== id)
  },
  CLEAR_USUARIO(state) {
    state.usuario = null
  },
  CLEAR_USUARIOS(state) {
    state.usuarios = []
  }
}

const actions = {
  /**
   * Busca todos os usuários
   */
  async fetchUsuarios({ commit }) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await ApiService.get('/Usuarios')
      
      commit('SET_USUARIOS', response.data)
      return response.data
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          error.message || 
                          'Erro ao carregar usuários'
      commit('SET_ERROR', errorMessage)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * Busca um usuário específico por ID
   * @param {string|number} id - ID do usuário
   */
  async fetchUsuarioById({ commit }, id) {
    if (!id) {
      const errorMessage = 'ID do usuário é obrigatório'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }

    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await ApiService.get(`/Usuarios/${id}`)
      
      let responseData = response.data || response
      
      // Se responseData for uma string JSON, faz o parse
      if (typeof responseData === 'string') {
        responseData = JSON.parse(responseData)
      }
      
      // A API retorna { usuario: {...}, permissoes: [...] }
      // Extrai os dados do usuário do wrapper
      let userData = responseData.usuario || responseData
      
      commit('SET_USUARIO', userData)
      commit('SET_LOADING', false)
      
      // Atualiza apenas o Vuex com os dados completos (sem tocar no localStorage)
      if (userData && typeof userData === 'object') {
        this.commit('auth/UPDATE_USER_DATA', userData, { root: true })
        
        // Popula as habilidades se existirem classes no retorno
        if (userData.classes && Array.isArray(userData.classes)) {
          await this.dispatch('habilidades/popularHabilidadesDoUsuario', userData.classes, { root: true })
          
          // Também popula o perfil com as habilidades
          const todasHabilidades = this.getters['habilidades/todasHabilidades']
          if (todasHabilidades && todasHabilidades.length > 0) {
            this.commit('perfil/SET_OUTRAS_HABILIDADES', todasHabilidades, { root: true })
          }
        }
        
        // Popula top habilidades se existirem no retorno
        if (userData.top_habilidades && Array.isArray(userData.top_habilidades)) {
          const topHabilidadesFormatadas = userData.top_habilidades.map(top => ({
            habilidade: top.skill_id,
            habilidadeNome: top.name,
            nivel: top.nivel,
            corSelecionada: top.cor,
            posicao: top.posicao
          }))
          this.commit('perfil/SET_TOP_HABILIDADES', topHabilidadesFormatadas, { root: true })
        }
      }
      
      return { success: true, data: response }
      
    } catch (error) {
      commit('SET_LOADING', false)
      
      // Extrai mensagem específica do erro
      let errorMessage = 'Erro ao buscar usuário'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Atualiza um usuário existente
   * @param {object} userData - Dados do usuário para atualizar
   */
  async updateUsuario({ commit }, userData) {
    if (!userData.id) {
      const errorMessage = 'ID do usuário é obrigatório para atualização'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }

    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await ApiService.put(`/Usuarios/${userData.id}`, userData)
      
      commit('UPDATE_USUARIO', response.data || response)
      commit('SET_LOADING', false)
      
      return { success: true, data: response }
      
    } catch (error) {
      commit('SET_LOADING', false)
      
      let errorMessage = 'Erro ao atualizar usuário'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Remove um usuário
   * @param {string|number} id - ID do usuário
   */
  async deleteUsuario({ commit }, id) {
    if (!id) {
      const errorMessage = 'ID do usuário é obrigatório'
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }

    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      await ApiService.delete(`/Usuarios/${id}`)
      
      commit('REMOVE_USUARIO', id)
      commit('SET_LOADING', false)
      
      return { success: true }
      
    } catch (error) {
      commit('SET_LOADING', false)
      
      let errorMessage = 'Erro ao remover usuário'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Busca usuários com filtros
   * @param {object} filters - Filtros de busca
   */
  async searchUsuarios({ commit }, filters = {}) {
    commit('SET_LOADING', true)
    commit('CLEAR_ERROR')
    
    try {
      const response = await ApiService.get('/Usuarios', filters)
      
      commit('SET_USUARIOS', response.data || response)
      commit('SET_LOADING', false)
      
      return { success: true, data: response }
      
    } catch (error) {
      commit('SET_LOADING', false)
      
      let errorMessage = 'Erro ao buscar usuários'
      
      if (error.message) {
        errorMessage = error.message
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (typeof error === 'string') {
        errorMessage = error
      }
      
      commit('SET_ERROR', errorMessage)
      return { success: false, error: errorMessage }
    }
  },

  /**
   * Limpa o erro
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },

  /**
   * Limpa o usuário atual
   */
  clearUsuario({ commit }) {
    commit('CLEAR_USUARIO')
  },

  /**
   * Limpa a lista de usuários
   */
  clearUsuarios({ commit }) {
    commit('CLEAR_USUARIOS')
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}